/** @jsx jsx */
import { jsx } from '@emotion/react';
import { ProgressBar, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { abbreviateNumber } from './abbreviateNumber';

export type Props = {
  /**
   * What the `UsageBar` is measuring.
   */
  label: string;
  /**
   * How much can be used.
   */
  max: number;
  /**
   * How much has been used.
   */
  value: number;
};

const Styles = labelStyles('UsageBar', {
  root: {
    display: 'block',

    // hack for overriding pdl colors
    'div > div': {
      backgroundColor: 'var(--zds-ui-primary, #847DFE)',
    },
  },
  text: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'auto 1fr',
    marginBottom: 'var(--zds-space-4, 4px)',
  },
  label: {
    font: `var(--zds-minimal-print-1, 400 12px/20px ${Typography.base.fontFamily})`,
    color: 'var(--zds-text-weaker, #574E4C)',
  },
  amounts: {
    font: `var(--zds-minimal-print-2, 500 12px/20px ${Typography.base.fontFamily})`,
    textAlign: 'right',
    color: 'var(--zds-text-default, #413735)',
  },
});

const formatNumber = (number: number): string => {
  const formatted =
    number > 9999 ? abbreviateNumber(number) : number.toLocaleString();
  return typeof formatted === 'string' ? formatted : 'Error';
};

/**
 * @private
 * Renders a `ProgressBar` for usage with labels.
 */
const UsageBar = (props: Props) => {
  const amount =
    props.max === Infinity
      ? 'Unlimited'
      : `${formatNumber(props.value)} / ${formatNumber(props.max)}`;
  return (
    <figure css={Styles.root} data-zds>
      <p css={Styles.text} data-zds>
        <span aria-hidden={true} css={Styles.label} data-zds>
          {props.label}
        </span>
        <span aria-hidden={true} css={Styles.amounts} data-zds>
          {amount}
        </span>
      </p>
      {props.max !== Infinity && (
        <ProgressBar max={props.max} value={props.value} />
      )}
    </figure>
  );
};

export default UsageBar;
